import React from "react";
import TextField from "@mui/material/TextField";
import Error from "../error/error";
import {ValidatedValue} from "../../../../helpers/validatedValue";
import {observer} from "mobx-react";
import "./textInput.scss";
import {IconButton, InputAdornment} from "@mui/material";

interface TextInputProps {
    onChange?: (event: any) => void;
    onBlur?: () => void;
    validatedValue: ValidatedValue<string | Date | null | number>;
    label: string;
    testId: string;
    className?: string;
    disabled?: boolean | false;
    endIcon?: JSX.Element;
    onClickEndIcon?: () => void;
    params?: any;
    type?: "text" | "password" | "number";
    withoutOnChange?: boolean | false;
    unit?: string;
    maxRows?: number;
    isAutoFocus?: boolean;
    offAutocomplete?: boolean;
    maxLength?: number;
    placeholderNumberType?: string;
}

function TextInput(props: TextInputProps) {
    return (
        <TextField
            {...props.params}
            variant="outlined"
            size="small"
            className={`text-input ${props.className || ""}`}
            value={getValue()}
            error={displayError()}
            helperText={getHelperText()}
            onChange={onChange}
            onKeyDown={() => {
                props.validatedValue.clearValidation();
            }}
            onBlur={onBlur}
            label={props.label}
            data-testid={props.testId}
            disabled={props.disabled}
            type={props.type || "text"}
            multiline={!!props.maxRows}
            maxRows={props.maxRows}
            placeholder={props.placeholderNumberType}
            InputProps={{
                endAdornment: (
                    <>
                        {props.endIcon && (
                            <InputAdornment position="end">
                                <IconButton onClick={props.onClickEndIcon} edge="end" disabled={props.disabled}>
                                    {props.endIcon}
                                </IconButton>
                            </InputAdornment>
                        )}
                        {props.unit && (
                            <InputAdornment position="end">
                                <p className="unit">
                                    {props.unit}
                                </p>
                            </InputAdornment>
                        )}
                    </>
                ),
                inputProps: inputProps()
            }}
            autoFocus={props.isAutoFocus}
            autoComplete={props.offAutocomplete ? "off" : props.testId}
        />
    );

    function inputProps() {
        if (props.type && props.type === "number") {
            return  {
                min: 0,
                step: 0.5
            };
        } else {
            if (props.params && props.params.inputProps) {
                return props.params.inputProps;
            }
        }
    }

    function onChange(event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) {
        if(props.withoutOnChange) return;

        const text = event.target.value;

        if (props.onChange) {
            props.onChange(text);
        }

        if (!props.maxLength || (props.maxLength && text.length <= props.maxLength)) {
            props.validatedValue.setValue(text);
        }
    }

    function onBlur() {
        props.validatedValue.validate();

        if (props.onBlur) {
            props.onBlur();
        }
    }

    function displayError(): boolean {
        return !!props.validatedValue.errorMessage;
    }

    function getHelperText() {
        return (displayError() ? <Error message={props.validatedValue.errorMessage}/> : "");
    }

    function getValue() {
        return props.validatedValue.value === 0 ? 0 : props.validatedValue.value || "";
    }
}

export default observer(TextInput);
